import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Calendar, Flame, Trophy,
  TrendingUp, Dumbbell, Scale, CalendarDays,
  Settings2, UtensilsCrossed, Zap,
  MessageSquare, Eye, EyeOff, MessageCircle,
} from "lucide-react";
import AccountNavbar from "./account/AccountNavbar";
import BottomNav from "../components/ui/BottomNav";

const API = process.env.REACT_APP_BACKEND_URL;

// ─── 6 DASHBOARD BLOKKEN ──────────────────────────────────────────────────────
const BLOCKS = [
  { id: "training",  label: "Training",  icon: Dumbbell,       path: "/account/training"  },
  { id: "voeding",   label: "Voeding",   icon: UtensilsCrossed, path: "/account/voeding"   },
  { id: "lichaam",   label: "Lichaam",   icon: Scale,          path: "/account/lichaam"   },
  { id: "voortgang", label: "Voortgang", icon: TrendingUp,     path: "/account/voortgang" },
  { id: "rooster",   label: "Rooster",   icon: CalendarDays,   path: "/account/rooster"   },
  { id: "community", label: "Community", icon: MessageCircle,  path: "/account/community" },
  { id: "profiel",   label: "Profiel",   icon: Settings2,      path: "/account/profiel"   },
];

export default function AccountPage({ kioskUser: kioskUserProp } = {}) {
  const { user: authUser, logout } = useAuth();
  const user = kioskUserProp || authUser;
  const navigate = useNavigate();

  const [voortgang,    setVoortgang]    = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [schemas,      setSchemas]      = useState([]);
  const [customSchemas,setCustomSchemas]= useState([]);
  const [bedrijfSchemas,setBedrijfSchemas]=useState([]);
  const [trainerBericht, setTrainerBericht] = useState(null);
  const [berichten,    setBerichten]    = useState([]);
  const [berichtInput, setBerichtInput] = useState("");
  const [sendingBericht, setSendingBericht] = useState(false);
  const [berichtenVerborgen, setBerichtenVerborgen] = useState(
    () => localStorage.getItem("fs_berichten_verborgen") === "true"
  );

  const isBedrijfsSporter = user?.role === "sporter";

  const loadData = useCallback(async () => {
    if (!user?.token) return;
    const h = { Authorization: `Bearer ${user.token}` };
    try {
      const [voortgangRes, progDashRes, schemaRes, customRes, bedrijfRes, berichtRes, berichtenRes] = await Promise.all([
        fetch(`${API}/api/sporter/voortgang`,         { headers: h }),
        fetch(`${API}/api/workout/progress-dashboard`,{ headers: h }),
        fetch(`${API}/api/my-schemas`,                { headers: h }),
        fetch(`${API}/api/my-custom-schemas`,         { headers: h }),
        fetch(`${API}/api/sporter/schemas`,           { headers: h }),
        fetch(`${API}/api/trainer-bericht`,           { headers: h }),
        fetch(`${API}/api/berichten`,                 { headers: h }),
      ]);
      if (voortgangRes.ok) setVoortgang(await voortgangRes.json());
      if (progDashRes.ok)  setProgressData(await progDashRes.json());
      if (schemaRes.ok)    setSchemas((await schemaRes.json()).schemas || []);
      if (customRes.ok)    setCustomSchemas((await customRes.json()).schemas || []);
      if (bedrijfRes.ok)   setBedrijfSchemas((await bedrijfRes.json()).schemas || []);
      if (berichtRes.ok) {
        const d = await berichtRes.json();
        if (d.bericht) setTrainerBericht(d);
      }
      if (berichtenRes.ok) setBerichten((await berichtenRes.json()).berichten || []);
    } catch (e) { console.error(e); }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleLogout = () => { logout(); navigate("/"); };

  const handleStuurBericht = async () => {
    if (!berichtInput.trim() || sendingBericht) return;
    setSendingBericht(true);
    try {
      const r = await fetch(`${API}/api/bericht`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({ tekst: berichtInput.trim() }),
      });
      const d = await r.json();
      if (d.id) setBerichten(prev => [...prev, d]);
      setBerichtInput("");
    } catch {}
    setSendingBericht(false);
  };

  const toggleBerichtenVerborgen = (v) => {
    setBerichtenVerborgen(v);
    localStorage.setItem("fs_berichten_verborgen", String(v));
  };

  if (!user) { navigate("/"); return null; }

  // Kiosk: B2B sporters → bedrijfSchemas; B2C → customSchemas + gekochte schemas
  const kioskSchemas = isBedrijfsSporter
    ? bedrijfSchemas
    : [
        ...customSchemas.map(s => ({ ...s, _type: "custom" })),
        ...schemas.map(s => ({ ...s, _type: "schema" })),
      ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <AccountNavbar onLogout={handleLogout} />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-28">

        {/* ── HERO SECTIE ── */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-5">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-black text-black shrink-0 shadow-lg"
              style={{ background: "linear-gradient(135deg, #C9A84C, #D4AF37)" }}
            >
              {user.name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "?"}
            </div>
            <div>
              <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-widest">Welkom terug</p>
              <h1 className="text-white font-black text-xl leading-tight">{user.name}</h1>
              <p className="text-zinc-500 text-xs mt-0.5 truncate max-w-[200px]">{user.email}</p>
            </div>
          </div>

          {/* KPI stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[
              {
                val: voortgang?.per_week?.length
                  ? (voortgang.per_week[voortgang.per_week.length - 1]?.count ?? 0) : 0,
                label: "Deze week", icon: <Calendar size={15} />,
              },
              { val: voortgang?.streak_weken ?? 0,   label: "Weken streak", icon: <Flame size={15} /> },
              { val: voortgang?.totaal_alltime ?? 0,  label: "Totaal ooit",  icon: <Trophy size={15} /> },
            ].map(({ val, label, icon }) => (
              <div key={label} className="bg-[#111] border border-white/8 rounded-2xl p-3 text-center"
                data-testid={`hero-stat-${label.replace(/ /g, "-").toLowerCase()}`}>
                <div className="text-[#C9A84C] flex justify-center mb-1">{icon}</div>
                <p className="text-2xl font-black text-white tabular-nums leading-none">{val}</p>
                <p className="text-zinc-600 text-[10px] font-semibold uppercase tracking-wide mt-1">{label}</p>
              </div>
            ))}
          </div>

          {/* Top PRs kaart */}
          {progressData?.oefeningen?.length > 0 && (
            <div className="bg-[#111] border border-white/8 rounded-2xl p-4 mb-4" data-testid="hero-pr-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Trophy size={13} style={{ color: "#C9A84C" }} />
                  <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-widest">Persoonlijke Records</p>
                </div>
                <button onClick={() => navigate("/progress")} data-testid="hero-bekijk-progress-btn"
                  className="text-zinc-600 hover:text-zinc-400 text-xs transition-colors">
                  Alle records →
                </button>
              </div>
              <div className="space-y-2">
                {progressData.oefeningen.slice(0, 3).map((oef, i) => (
                  <div key={i} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black text-black shrink-0"
                        style={{ background: i === 0 ? "#FFD700" : i === 1 ? "#C0C0C0" : "#CD7F32" }}>
                        {i + 1}
                      </span>
                      <p className="text-zinc-300 text-sm truncate">{oef.naam}</p>
                    </div>
                    <span className="text-[#C9A84C] font-black text-sm tabular-nums shrink-0">{oef.beste_1rm} kg</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── KIOSK MODUS: Schema's direct starten ── */}
        {kioskUserProp && (
          <div className="mb-6" data-testid="kiosk-schemas-section">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: "#C9A84C20" }}>
                <Zap size={13} style={{ color: "#C9A84C" }} />
              </div>
              <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">Schema's starten</p>
            </div>
            {kioskSchemas.length === 0 ? (
              <div className="bg-[#111] border border-white/8 rounded-2xl p-5 text-center">
                <Dumbbell size={24} className="text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm font-semibold">Geen schema's beschikbaar</p>
              </div>
            ) : (
              <div className="space-y-3">
                {kioskSchemas.map((s) => (
                  <div key={s.id} className="bg-[#111] border border-[#C9A84C]/20 rounded-2xl overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
                      <div className="w-8 h-8 rounded-xl bg-[#C9A84C]/10 flex items-center justify-center shrink-0">
                        <Dumbbell size={15} className="text-[#C9A84C]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-bold text-sm truncate">{s.naam}</p>
                        <p className="text-zinc-600 text-xs">
                          {s.trainingsdagen ? `${s.trainingsdagen}x per week` : `${s.dagen?.length || 0} trainingsdagen`}
                        </p>
                      </div>
                    </div>
                    {s.dagen && s.dagen.length > 0 && (
                      <div className="p-3 flex flex-wrap gap-2">
                        {s.dagen.map((dag, i) => (
                          <button key={i}
                            onClick={() => {
                              sessionStorage.setItem("kiosk_token", kioskUserProp.token);
                              sessionStorage.setItem("kiosk_user_id", kioskUserProp.id || kioskUserProp._id || "");
                              navigate(`/workout-custom/${s.id}/${i}`);
                            }}
                            data-testid={`kiosk-start-${s.id}-dag-${i}`}
                            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-black text-black transition-all hover:scale-[1.02] active:scale-95"
                            style={{ background: "#C9A84C" }}>
                            <Zap size={12} />
                            {dag.naam || dag.dag || `Dag ${i + 1}`}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── BERICHTEN MET TRAINER — alleen B2C gebruikers ── */}
        {!kioskUserProp && !isBedrijfsSporter && (berichten.length > 0 || trainerBericht?.bericht) && (
          berichtenVerborgen ? (
            <div className="flex items-center justify-between mb-6 px-4 py-3 rounded-2xl border border-white/6 bg-[#111]">
              <div className="flex items-center gap-2">
                <MessageSquare size={13} style={{ color: "#C9A84C" }} />
                <span className="text-zinc-500 text-sm">Berichten van je trainer</span>
                {berichten.filter(b => !b.gelezen_door_user).length > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full leading-none">
                    {berichten.filter(b => !b.gelezen_door_user).length}
                  </span>
                )}
              </div>
              <button onClick={() => toggleBerichtenVerborgen(false)} data-testid="toon-berichten-btn"
                className="flex items-center gap-1.5 text-xs font-semibold text-zinc-400 hover:text-white transition-colors">
                <Eye size={13} /> Toon berichten
              </button>
            </div>
          ) : (
            <div className="account-card mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MessageSquare size={14} style={{ color: "#C9A84C" }} />
                  <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">Berichten van je trainer</p>
                </div>
                <button onClick={() => toggleBerichtenVerborgen(true)} data-testid="verberg-berichten-btn"
                  className="flex items-center gap-1.5 text-xs text-zinc-600 hover:text-zinc-300 transition-colors">
                  <EyeOff size={12} /> Verbergen
                </button>
              </div>
              <div className="space-y-3 mb-4 max-h-80 overflow-y-auto pr-1">
                {berichten.length === 0 && trainerBericht?.bericht && (
                  <div className="flex justify-start">
                    <div className="max-w-[80%]">
                      <div className="px-3.5 py-2.5 rounded-2xl rounded-bl-sm text-sm leading-relaxed bg-white/6 text-zinc-200 border border-white/6">
                        {trainerBericht.bericht}
                      </div>
                      <p className="text-[10px] text-zinc-600 mt-1">
                        {trainerBericht.datum ? new Date(trainerBericht.datum).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) : ""}
                      </p>
                    </div>
                  </div>
                )}
                {berichten.map(b => {
                  const isMij = b.van === "user";
                  return (
                    <div key={b.id} className={`flex ${isMij ? "justify-end" : "justify-start"}`}>
                      <div className="max-w-[80%]">
                        <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                          isMij ? "text-black rounded-br-sm" : "bg-white/6 text-zinc-200 rounded-bl-sm border border-white/6"
                        }`} style={isMij ? { background: "#C9A84C" } : {}}>
                          {b.tekst}
                        </div>
                        <p className={`text-[10px] text-zinc-600 mt-1 ${isMij ? "text-right" : "text-left"}`}>
                          {new Date(b.datum).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <input type="text" value={berichtInput} onChange={e => setBerichtInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleStuurBericht()}
                  placeholder="Typ een bericht aan je trainer..."
                  data-testid="user-bericht-input"
                  className="flex-1 bg-[#1a1a1a] border border-white/8 rounded-xl px-3.5 py-2.5 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/40" />
                <button onClick={handleStuurBericht} disabled={!berichtInput.trim() || sendingBericht}
                  data-testid="user-stuur-bericht-btn"
                  className="px-4 py-2.5 rounded-xl text-sm font-bold text-black disabled:opacity-40 transition-opacity"
                  style={{ background: "#C9A84C" }}>
                  {sendingBericht ? "..." : "Stuur"}
                </button>
              </div>
            </div>
          )
        )}

        {/* ── 6-BLOKKEN DASHBOARD (alleen in normale modus) ── */}
        {!kioskUserProp && (
          <div className="grid grid-cols-2 gap-3" data-testid="dashboard-blocks">
            {BLOCKS.map(block => {
              const Icon = block.icon;
              return (
                <button
                  key={block.id}
                  onClick={() => navigate(block.path)}
                  data-testid={`block-${block.id}`}
                  className="bg-[#111] border border-white/8 rounded-2xl flex flex-col items-center justify-center gap-3 py-7 px-4 hover:border-[#C9A84C]/40 hover:bg-[#161616] transition-all active:scale-95 group"
                >
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center transition-all group-hover:scale-105"
                    style={{ background: "rgba(201,168,76,0.12)" }}>
                    <Icon size={22} style={{ color: "#C9A84C" }} />
                  </div>
                  <span className="text-white font-bold text-sm tracking-wide">{block.label}</span>
                </button>
              );
            })}
          </div>
        )}

      </div>

      {!kioskUserProp && <BottomNav />}
    </div>
  );
}
